import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { catchError, map, Observable, of } from 'rxjs';

import type {
  FeatureTourReadinessSource,
  FeatureTourReadinessStatus,
} from '@customer-portal/data-access/feature-announcements';

import type { FeatureTourReadinessGateway } from './feature-tour-readiness.gateway';

const unsafePathSegments = new Set(['__proto__', 'constructor', 'prototype']);

function isSafePath(path: string): boolean {
  const segments = path.split('.');

  return (
    segments.length > 0 &&
    segments.every(
      (segment) =>
        /^[A-Za-z][A-Za-z0-9]*$/.test(segment) &&
        !unsafePathSegments.has(segment),
    )
  );
}

function getPathValue(source: unknown, path: string): unknown {
  if (!isSafePath(path)) {
    return undefined;
  }

  return path.split('.').reduce<unknown>((value, segment) => {
    if (
      value === null ||
      (typeof value !== 'object' && !Array.isArray(value)) ||
      !Object.prototype.hasOwnProperty.call(value, segment)
    ) {
      return undefined;
    }

    return (value as Record<string, unknown>)[segment];
  }, source);
}

function isPopulated(value: unknown): boolean {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0;
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length > 0;
  }

  return false;
}

function getReadinessStatus(
  state: Record<string, unknown>,
  source: FeatureTourReadinessSource,
): FeatureTourReadinessStatus {
  if (!Object.prototype.hasOwnProperty.call(state, source.stateKey)) {
    return 'unavailable';
  }

  const slice = state[source.stateKey];
  const loading = source.loadingPaths.some((path) =>
    Boolean(getPathValue(slice, path)),
  );

  if (loading) {
    return 'loading';
  }

  const hasError = source.errorPaths.some((path) => {
    const value = getPathValue(slice, path);

    return (
      value !== undefined && value !== null && value !== false && value !== ''
    );
  });

  if (hasError) {
    return 'unavailable';
  }

  if (!source.loadedPaths.every((path) => Boolean(getPathValue(slice, path)))) {
    return 'loading';
  }

  return source.dataPaths.every((path) =>
    isPopulated(getPathValue(slice, path)),
  )
    ? 'ready'
    : 'unavailable';
}

@Injectable()
export class NgxsFeatureTourReadinessGateway
  implements FeatureTourReadinessGateway
{
  constructor(private readonly store: Store) {}

  watch(
    source: FeatureTourReadinessSource,
  ): Observable<FeatureTourReadinessStatus> {
    const configuredPaths = [
      ...source.loadingPaths,
      ...source.loadedPaths,
      ...source.errorPaths,
      ...source.dataPaths,
    ];

    if (
      !/^[A-Za-z][A-Za-z0-9]*$/.test(source.stateKey) ||
      configuredPaths.length === 0 ||
      configuredPaths.some((path) => !isSafePath(path))
    ) {
      return of('unavailable');
    }

    return this.store
      .select((state: Record<string, unknown>) => state)
      .pipe(
        map((state) => getReadinessStatus(state, source)),
        catchError(() => of<FeatureTourReadinessStatus>('unavailable')),
      );
  }
}
